// Mail V2 — 内部导航(220px),从系统内容区开始,不替换全局主导航。
import React from "react";
import {
  Inbox,
  Star,
  Send,
  FileText,
  AlertOctagon,
  Trash2,
  Archive,
  PenSquare,
} from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { type MailAccount, type MailFolder } from "../data/demoMailData";
import type { FolderCounts, FolderCountKey } from "../hooks/useFolderCounts";

interface MailV2SidebarLabel {
  id: string;
  name: string;
  color: string | null;
  count?: number;
}

interface MailV2SidebarProps {
  folders: MailFolder[];
  labels: MailV2SidebarLabel[];
  selectedFolderId: string;
  selectedLabelId: string | null;
  account: MailAccount;
  onSelectFolder: (id: string) => void;
  onSelectLabel: (id: string) => void;
  onCompose: () => void;
  // Stage Mail-V2 Step 16: 真实 counts 覆盖 demo count (real mode 下注入)。
  folderCounts?: FolderCounts;
  // Stage Mail-V2 Step 17/19: real mode 走真 labels + 创建
  onCreateLabel?: (name: string) => Promise<void> | void;
}

const ICON_MAP = {
  Inbox,
  Star,
  Send,
  FileText,
  AlertOctagon,
  Trash2,
  Archive,
} as const;

const FOLDER_COUNT_KEY: Partial<Record<string, FolderCountKey>> = {
  inbox: "inbox",
  starred: "starred",
  sent: "sent",
  drafts: "drafts",
  spam: "spam",
  trash: "trash",
  all: "total",
};

export function MailV2Sidebar({
  folders,
  selectedFolderId,
  account,
  onSelectFolder,
  onCompose,
  folderCounts,
}: MailV2SidebarProps) {
  const accountTypeLabel =
    account.accountType === "personal" ? "個人メール" : account.accountType === "shared" ? "共有メール" : "その他";

  return (
    <div className="mv2-sidebar">
      {/* Compose Card */}
      <div className="mv2-sidebar-card">
        <div className="mv2-sidebar-compose">
          <Button
            variant="primary"
            iconLeft={<PenSquare size={16} />}
            onClick={onCompose}
            full
          >
            メールを作成
          </Button>
        </div>
        <div className="mv2-sidebar-account-email">{account.email}</div>
        <div className="mv2-sidebar-account-type">{accountTypeLabel}</div>
      </div>

      {/* Folder Card */}
      <div className="mv2-sidebar-card">
        <div className="mv2-sidebar-section">
          {folders.map((folder) => {
            const Icon = ICON_MAP[folder.iconName];
            const isActive = selectedFolderId === folder.id;
            const countKey = FOLDER_COUNT_KEY[folder.id];
            const realCount = folderCounts
              ? folderCounts[countKey ?? (folder.id as FolderCountKey)]
              : undefined;
            const displayCount = realCount !== undefined ? realCount.total : folder.count;
            return (
              <div
                key={folder.id}
                className={`mv2-sidebar-item${isActive ? " active" : ""}`}
                onClick={() => onSelectFolder(folder.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectFolder(folder.id);
                  }
                }}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="mv2-sidebar-item-icon">
                  <Icon size={18} />
                </span>
                <span>{folder.name}</span>
                {typeof displayCount === "number" && displayCount > 0 && (
                  <span className="mv2-sidebar-item-count">{displayCount}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
